import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// 1. Initialize Lenis Smooth Scroll
const lenis = new Lenis({
	duration: 1.4,
	easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
	smoothWheel: true,
	wheelMultiplier: 1,
	touchMultiplier: 2,
	infinite: false,
});

// 2. Synchronize Lenis with GSAP ScrollTrigger
lenis.on('scroll', ScrollTrigger.update);

gsap.ticker.add((time) => {
	lenis.raf(time * 1000);
});

gsap.ticker.lagSmoothing(0);

// Expose globally for modular scripts
window.lenis = lenis;
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;

// 3. Global & Page Animations
document.addEventListener('DOMContentLoaded', () => {
	// A) Global: Navbar entrance from top (duration: 0.3s)
	const navbar = document.querySelector('#main-nav') || document.querySelector('.navbar');
	if (navbar) {
		gsap.fromTo(
			navbar,
			{
				yPercent: -100,
				opacity: 0,
			},
			{
				yPercent: 0,
				opacity: 1,
				duration: 0.3,
				ease: 'power2.out',
				onComplete: () => {
					navbar.style.opacity = '1';
					navbar.style.transform = 'none';
				},
			}
		);
	}

	// B) Hero Home: Sequence animations
	const heroHome = document.querySelector('.hero-section-home') || document.querySelector('.hero-section');
	if (heroHome) {
		// 1. Background Brightness & Blur transition
		gsap.fromTo(
			heroHome,
			{
				filter: 'brightness(200%) blur(8px)',
			},
			{
				filter: 'brightness(100%) blur(0px)',
				duration: 1.4,
				delay: 0.1,
				ease: 'power4.out',
			}
		);

		// 2. Title entrance from bottom (delay: 0.2s)
		const heroTitle = heroHome.querySelector('h1');
		if (heroTitle) {
			gsap.fromTo(
				heroTitle,
				{
					y: 40,
					opacity: 0,
				},
				{
					y: 0,
					opacity: 1,
					duration: 0.9,
					delay: 0.2,
					ease: 'back.out',
					onComplete: () => {
						heroTitle.style.opacity = '1';
						heroTitle.style.transform = 'none';
					},
				}
			);
		}

		// 3. Featured product card / slider entrance from bottom (delay: 0.3s)
		const heroWrprod = heroHome.querySelector('.wrprod');
		if (heroWrprod) {
			gsap.fromTo(
				heroWrprod,
				{
					y: 40,
					opacity: 0,
				},
				{
					y: 0,
					opacity: 1,
					duration: 0.9,
					delay: 0.4,
					ease: 'back.out',
					onComplete: () => {
						heroWrprod.style.opacity = '1';
						heroWrprod.style.transform = 'none';
					},
				}
			);
		}
	}

	// C) Solutions Bar: Scroll reveal
	const solutionsBar = document.querySelector('.solutions-bar');
	if (solutionsBar) {
		const solutionsItems = solutionsBar.querySelectorAll('img, div');
		if (solutionsItems.length) {
			gsap.fromTo(
				solutionsItems,
				{
					y: 30,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: solutionsBar,
						start: 'top 85%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0.15,
					ease: 'power3.out',
					onComplete: () => {
						solutionsItems.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}
	}

	// D) Features Grid: Staggered reveal of feature cards
	const featuresGrid = document.querySelector('.features-grid');
	if (featuresGrid) {
		const featureItems = featuresGrid.querySelectorAll('.feature-item');
		if (featureItems.length) {
			gsap.fromTo(
				featureItems,
				{
					y: 40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: featuresGrid,
						start: 'top 80%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.7,
					stagger: 0.08,
					ease: 'power3.out',
					onComplete: () => {
						featureItems.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}
	}

	// E) History Section: Staggered reveal of columns and cards
	const historySection = document.querySelector('.history-section');
	if (historySection) {
		// 1. Columnas de texto e imagen principal
		const historyCols = historySection.querySelectorAll('.row > div:not(.card)');
		if (historyCols.length) {
			gsap.fromTo(
				historyCols,
				{
					y: 40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: historySection,
						start: 'top 80%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0.15,
					ease: 'power3.out',
					onComplete: () => {
						historyCols.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}

		// 2. Tarjetas inferiores / cards
		const historyCards = historySection.querySelectorAll('.card');
		if (historyCards.length) {
			gsap.fromTo(
				historyCards,
				{
					y: 40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: historyCards[0],
						start: 'top 85%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0.12,
					ease: 'power3.out',
					onComplete: () => {
						historyCards.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}
	}

	// F) News Section (Mundo Scorza): Staggered reveal of header and news cards
	const newsSection = document.querySelector('.news-section');
	if (newsSection) {
		// Encabezado
		const newsHeader = newsSection.querySelector('.d-flex.justify-content-between');
		if (newsHeader) {
			gsap.fromTo(
				newsHeader,
				{
					y: 30,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: newsSection,
						start: 'top 85%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.8,
					ease: 'power3.out',
					onComplete: () => {
						newsHeader.style.opacity = '1';
						newsHeader.style.transform = 'none';
					},
				}
			);
		}

		// Tarjetas de noticias
		const newsItems = newsSection.querySelectorAll('.new-item');
		if (newsItems.length) {
			gsap.fromTo(
				newsItems,
				{
					y: 40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: newsSection,
						start: 'top 80%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0.15,
					ease: 'power3.out',
					onComplete: () => {
						newsItems.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}
	}

	// G) Footer: Staggered reveal of contact info and form
	const footer = document.querySelector('.site-footer, footer');
	if (footer) {
		const footerCols = footer.querySelectorAll('.prefooter .row > div');
		if (footerCols.length) {
			gsap.fromTo(
				footerCols,
				{
					y: 40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: footer,
						start: 'top 85%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0.2,
					ease: 'power3.out',
					onComplete: () => {
						footerCols.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}
	}

	// H) Empresa Page - CTO Section: Staggered reveal of photo and quote
	const ctoSection = document.querySelector('section.cto');
	if (ctoSection) {
		const ctoCols = ctoSection.querySelectorAll('.row > div');
		if (ctoCols.length) {
			gsap.fromTo(
				ctoCols,
				{
					y: 40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: ctoSection,
						start: 'top 80%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.9,
					stagger: 0.2,
					ease: 'power3.out',
					onComplete: () => {
						ctoCols.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}
	}

	// I) Trabaja Page: Staggered reveal of main content (fade-up) & sidebar (left-to-right)
	const trabajaSection = document.querySelector('section.trabaja');
	if (trabajaSection) {
		// 1. Columna principal (de abajo hacia arriba)
		const mainElements = trabajaSection.querySelectorAll('.col-lg-8 > *');
		if (mainElements.length) {
			gsap.fromTo(
				mainElements,
				{
					y: 40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: trabajaSection,
						start: 'top 80%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0.15,
					ease: 'power3.out',
					onComplete: () => {
						mainElements.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}

		// 2. Columna lateral (de izquierda a derecha)
		const sidebarItems = trabajaSection.querySelectorAll('.sidebar h5, .sidebar .card');
		if (sidebarItems.length) {
			gsap.fromTo(
				sidebarItems,
				{
					x: -40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: trabajaSection.querySelector('.sidebar'),
						start: 'top 85%',
						toggleActions: 'play none none none',
					},
					x: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0.12,
					ease: 'power3.out',
					onComplete: () => {
						sidebarItems.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}
	}

	// J) Single Detail (Producto / Noticia): Hero from top, Header from bottom, Content staggered
	const singleContainer = document.querySelector('.single main#main');
	if (singleContainer) {
		// 1. Imagen Hero desde arriba con efecto de brillo (delay: 0.15s)
		const singleHero = singleContainer.querySelector('.hero');
		if (singleHero) {
			gsap.fromTo(
				singleHero,
				{
					y: -40,
					opacity: 0,
					filter: 'brightness(200%)',
				},
				{
					y: 0,
					opacity: 1,
					filter: 'brightness(100%)',
					duration: 1.2,
					delay: 0.15,
					ease: 'power3.out',
					onComplete: () => {
						singleHero.style.opacity = '1';
						singleHero.style.transform = 'none';
						singleHero.style.filter = 'none';
					},
				}
			);
		}

		// 2. Encabezado / Título (entry-header) desde abajo (delay: 0.25s)
		const entryHeaders = singleContainer.querySelectorAll('.entry-header');
		if (entryHeaders.length) {
			gsap.fromTo(
				entryHeaders,
				{
					y: 40,
					opacity: 0,
				},
				{
					y: 0,
					opacity: 1,
					duration: 0.9,
					delay: 0.25,
					ease: 'power3.out',
					onComplete: () => {
						entryHeaders.forEach((header) => {
							header.style.opacity = '1';
							header.style.transform = 'none';
						});
					},
				}
			);
		}

		// 3. Contenido del producto / noticia (entry-content) escalonado desde abajo
		const contentElements = singleContainer.querySelectorAll('.entry-content > *');
		if (contentElements.length) {
			gsap.fromTo(
				contentElements,
				{
					y: 40,
					opacity: 0,
				},
				{
					scrollTrigger: {
						trigger: '.entry-content',
						start: 'top 85%',
						toggleActions: 'play none none none',
					},
					y: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0.12,
					ease: 'power3.out',
					onComplete: () => {
						contentElements.forEach((el) => {
							el.style.opacity = '1';
							el.style.transform = 'none';
						});
					},
				}
			);
		}
	}

	// K) Generic Reveal elements with [data-gsap="fade-up"]
	gsap.utils.toArray('[data-gsap="fade-up"]').forEach((elem) => {
		gsap.fromTo(
			elem,
			{
				y: 40,
				opacity: 0,
			},
			{
				scrollTrigger: {
					trigger: elem,
					start: 'top 85%',
					toggleActions: 'play none none none',
				},
				y: 0,
				opacity: 1,
				duration: 0.8,
				ease: 'power3.out',
				onComplete: () => {
					elem.style.opacity = '1';
					elem.style.transform = 'none';
				},
			}
		);
	});

	// D) Reveal elements with [data-gsap="fade-in"]
	gsap.utils.toArray('[data-gsap="fade-in"]').forEach((elem) => {
		gsap.from(elem, {
			scrollTrigger: {
				trigger: elem,
				start: 'top 85%',
				toggleActions: 'play none none none',
			},
			opacity: 0,
			duration: 0.9,
			ease: 'power2.out',
		});
	});
});